/**
 * TypeScript types for Body Measurements Tracking feature.
 *
 * Defines interfaces for measurement data, API requests/responses,
 * and related types for the body measurements tracking system.
 */

/**
 * Unit types for measurements.
 */
export type WeightUnit = "kg" | "lbs";
export type LengthUnit = "cm" | "inches";

/**
 * Individual measurement value with unit.
 */
export interface MeasurementValue {
  value: number;
  unit: WeightUnit | LengthUnit;
}

/**
 * Complete set of measurements for a single entry.
 * Required measurements: weight, height, chest, waist, hip
 * Optional measurements: left_arm, right_arm, left_thigh, right_thigh
 */
export interface Measurements {
  weight: MeasurementValue;
  height: MeasurementValue;
  chest: MeasurementValue;
  waist: MeasurementValue;
  hip: MeasurementValue;
  left_arm?: MeasurementValue;
  right_arm?: MeasurementValue;
  left_thigh?: MeasurementValue;
  right_thigh?: MeasurementValue;
}

/**
 * Body measurement entry stored in database.
 */
export interface BodyMeasurement {
  id: string;
  _id?: string;  // MongoDB ObjectId (optional for compatibility)
  user_id: string;
  date: string;  // ISO date string (YYYY-MM-DD)
  measurements: Measurements;
  waist_hip_ratio: number;  // Waist-Hip Ratio (WHR)
  created_at: string;  // ISO datetime string
  updated_at: string;  // ISO datetime string
}

/**
 * Input payload for creating a new measurement.
 */
export interface MeasurementInput {
  date?: string;  // ISO date string, defaults to today if not provided
  measurements: Measurements;
}

/**
 * Input payload for updating an existing measurement.
 */
export interface MeasurementUpdateInput {
  measurements: Measurements;
}

/**
 * Response from list measurements endpoint.
 */
export interface ListMeasurementsResponse {
  measurements: BodyMeasurement[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Trend direction indicators.
 */
export type TrendDirection = "increasing" | "decreasing" | "stable";

/**
 * Statistics for a single measurement type over a time period.
 */
export interface MeasurementStatistics {
  average: number;
  min: number;
  max: number;
  unit: WeightUnit | LengthUnit;
  trend: TrendDirection;
  change_percentage: number;  // % change from first to last in range
}

/**
 * Trend data point for visualization.
 */
export interface TrendDataPoint {
  date: string;  // ISO date string
  weight?: MeasurementValue;
  waist?: MeasurementValue;
  hip?: MeasurementValue;
  chest?: MeasurementValue;
  left_arm?: MeasurementValue;
  right_arm?: MeasurementValue;
  left_thigh?: MeasurementValue;
  right_thigh?: MeasurementValue;
}

/**
 * Response from trends endpoint.
 */
export interface TrendsResponse {
  trend_data: TrendDataPoint[];
  statistics: {
    weight?: MeasurementStatistics;
    waist?: MeasurementStatistics;
    hip?: MeasurementStatistics;
    chest?: MeasurementStatistics;
    left_arm?: MeasurementStatistics;
    right_arm?: MeasurementStatistics;
    left_thigh?: MeasurementStatistics;
    right_thigh?: MeasurementStatistics;
  };
  entry_count: number;
  date_range: {
    start: string;
    end: string;
  };
  message?: string;  // Present when insufficient data
}

/**
 * Query parameters for list measurements endpoint.
 */
export interface ListMeasurementsParams {
  days?: number;      // Filter: last N days (default: 30, max: 365)
  limit?: number;     // Max results (default: 100, max: 100)
  offset?: number;    // Pagination offset (default: 0)
}

/**
 * Query parameters for trends endpoint.
 */
export interface TrendsParams {
  days?: number;      // Time range: 7, 30, 60, or 90 days (default: 30)
}

/**
 * Validation error from API.
 */
export interface MeasurementValidationError {
  detail: string;
  change_percentage?: number;
  requires_confirmation?: boolean;
}

/**
 * Form state for measurement entry.
 */
export interface MeasurementFormData {
  date: string;
  weight_value: string;
  weight_unit: WeightUnit;
  height_value: string;
  height_unit: LengthUnit;
  chest_value: string;
  chest_unit: LengthUnit;
  waist_value: string;
  waist_unit: LengthUnit;
  hip_value: string;
  hip_unit: LengthUnit;
  left_arm_value?: string;
  left_arm_unit?: LengthUnit;
  right_arm_value?: string;
  right_arm_unit?: LengthUnit;
  left_thigh_value?: string;
  left_thigh_unit?: LengthUnit;
  right_thigh_value?: string;
  right_thigh_unit?: LengthUnit;
}

/**
 * Measurement change from previous entry.
 */
export interface MeasurementChange {
  current: number;
  previous: number;
  difference: number;
  percentage: number;
  direction: "up" | "down" | "same";
}

/**
 * Validation ranges for measurements.
 */
export const VALIDATION_RANGES = {
  weight: {
    kg: { min: 30, max: 300 },
    lbs: { min: 66, max: 661 }
  },
  height: {
    cm: { min: 100, max: 250 },
    inches: { min: 39, max: 98 }
  },
  circumference: {
    cm: { min: 20, max: 200 },
    inches: { min: 7.9, max: 78.7 }
  }
} as const;

/**
 * Unit conversion constants.
 */
export const CONVERSION_FACTORS = {
  KG_TO_LBS: 2.20462262,
  LBS_TO_KG: 0.45359237,
  CM_TO_INCHES: 0.393701,
  INCHES_TO_CM: 2.54
} as const;

/**
 * Convert weight between units.
 */
export function convertWeight(value: number, fromUnit: WeightUnit, toUnit: WeightUnit): number {
  if (fromUnit === toUnit) return value;
  if (fromUnit === 'kg' && toUnit === 'lbs') {
    return Math.round(value * CONVERSION_FACTORS.KG_TO_LBS * 10) / 10;
  }
  return Math.round(value * CONVERSION_FACTORS.LBS_TO_KG * 10) / 10;
}

/**
 * Convert length between units.
 */
export function convertLength(value: number, fromUnit: LengthUnit, toUnit: LengthUnit): number {
  if (fromUnit === toUnit) return value;
  if (fromUnit === 'cm' && toUnit === 'inches') {
    return Math.round(value * CONVERSION_FACTORS.CM_TO_INCHES * 10) / 10;
  }
  return Math.round(value * CONVERSION_FACTORS.INCHES_TO_CM * 10) / 10;
}

/**
 * Convert a measurement value to the target unit system.
 */
export function convertMeasurementValue(
  measurement: MeasurementValue,
  targetUnit: WeightUnit | LengthUnit
): MeasurementValue {
  const currentUnit = measurement.unit;
  
  // Weight conversion
  if ((currentUnit === 'kg' || currentUnit === 'lbs') && 
      (targetUnit === 'kg' || targetUnit === 'lbs')) {
    return {
      value: convertWeight(measurement.value, currentUnit as WeightUnit, targetUnit as WeightUnit),
      unit: targetUnit as WeightUnit
    };
  }
  
  // Length conversion
  if ((currentUnit === 'cm' || currentUnit === 'inches') && 
      (targetUnit === 'cm' || targetUnit === 'inches')) {
    return {
      value: convertLength(measurement.value, currentUnit as LengthUnit, targetUnit as LengthUnit),
      unit: targetUnit as LengthUnit
    };
  }
  
  return measurement;
}
